/**
 * Timezone helpers for location-creation forms.
 *
 * Two responsibilities:
 *
 * 1. Provide a curated set of IANA timezones for the dropdown - US-
 *    centric (matches the dealership/fleet customer base) plus UTC for
 *    back-office / non-US. The label includes the common abbreviation
 *    so a non-tech admin recognises "EST" before realising it's
 *    ``America/New_York``. Keep tight; add only when a real customer
 *    asks.
 *
 * 2. Default new locations to the *creator's* browser timezone so a
 *    West-Coast admin spinning up a Denver location doesn't have to
 *    manually pick "America/Denver" every time. ``browserTimezone()``
 *    wraps ``Intl.DateTimeFormat().resolvedOptions().timeZone`` with a
 *    UTC fallback for the (rare) browsers/runtimes that don't surface
 *    a value. ``timezoneChoicesWithBrowser()`` adds the detected zone
 *    to the curated list when it's not already there, so the dropdown
 *    can render the default selection without a blank option.
 */

export interface TimezoneChoice {
  value: string;
  label: string;
}

/** Curated US-centric list. Order is the order users see in the
 *  dropdown - prefer Eastern → Central → Mountain → Pacific → outliers. */
export const CURATED_TIMEZONES: ReadonlyArray<TimezoneChoice> = [
  { value: "America/New_York", label: "Eastern (America/New_York)" },
  { value: "America/Chicago", label: "Central (America/Chicago)" },
  { value: "America/Denver", label: "Mountain (America/Denver)" },
  { value: "America/Phoenix", label: "Arizona - no DST (America/Phoenix)" },
  { value: "America/Los_Angeles", label: "Pacific (America/Los_Angeles)" },
  { value: "America/Anchorage", label: "Alaska (America/Anchorage)" },
  { value: "Pacific/Honolulu", label: "Hawaii (Pacific/Honolulu)" },
  { value: "UTC", label: "UTC" },
];

/**
 * The IANA name for the browser's current timezone. Returns ``"UTC"``
 * when the runtime doesn't expose a ``timeZone`` (very old browsers,
 * SSR contexts, or test environments where ``Intl`` is stubbed).
 */
export function browserTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz && typeof tz === "string" ? tz : "UTC";
  } catch {
    return "UTC";
  }
}

/**
 * Curated list with the browser's timezone appended (under the label
 * ``"<zone> (your browser)"``) when it isn't already in the curated
 * set. Use this for create-location dropdowns so the default
 * selection always matches a real option - otherwise React would
 * render a blank ``<select>`` because no ``<option>`` matches.
 */
export function timezoneChoicesWithBrowser(): TimezoneChoice[] {
  return timezoneChoicesIncluding([]);
}

/**
 * Curated list extended with the browser's timezone AND any extra
 * IANA names the caller passes in (typically the existing value of
 * an edit form). Each extra zone that isn't already in the curated
 * set or covered by an earlier extra is appended. Use this on edit
 * forms where the saved value may be outside the curated set - the
 * dropdown still renders that value as a selectable option so the
 * select doesn't go blank on first paint.
 */
export function timezoneChoicesIncluding(extras: ReadonlyArray<string>): TimezoneChoice[] {
  const result: TimezoneChoice[] = [...CURATED_TIMEZONES];
  const seen = new Set(result.map((tz) => tz.value));
  const candidates = [browserTimezone(), ...extras];
  for (const zone of candidates) {
    if (!zone || seen.has(zone)) continue;
    seen.add(zone);
    // Browser pick gets a "(your browser)" hint so the user knows
    // why it's there; user-supplied extras (saved values) just show
    // the bare IANA name.
    const label = zone === browserTimezone() ? `${zone} (your browser)` : zone;
    result.push({ value: zone, label });
  }
  return result;
}
